import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useU2000AxiosAuthenticated from "../u2000Core/axios/U2000UseAxiosAuthenticated";
import { U3143ContextProvider } from "./U3143Context";
import { Outlet } from "react-router-dom";
import i18next from "i18next";
import { doInitLU, districtCadastreHandleApiError } from "./U3143Service";
import { useU2000InitLUHandlers } from "../u2000Core/hooks/handler/U2000UseInitLUHandlers";
import { U2000HandleApiError } from "../u2000Core/axios/U2000HandleApiError";
import { useToast } from "../u2000Core/context/toast/U2000ToastContext";
import { useU2000LogicalUnitContext } from "../u2000Core/context/logicalUnit/U2000LogicalUnitContext";

export const NS = "u3143";

export const U3143ROLES = {
  READ: "U3143:READ",
  ADD: "U3143:ADD",
  EDIT: "U3143:EDIT",
  DELETE: "U3143:DELETE",
  AUDITS: "U3143:AUDITS",
  EXPORT: "U3143:EXPORT",
};
export type U3143ROLES = (typeof U3143ROLES)[keyof typeof U3143ROLES];

export interface U3143InitLU {
  launchSearch: boolean;
  hideSearch: boolean;
  nbRecordPerPage: number;
}

const U3143: React.FC = () => {
  const { i18n } = useTranslation();
  const [i18nReady, setI18nReady] = useState<boolean>(false);
  const { t } = useTranslation();
  const toast = useToast();
  const [initLUReady, setInitLUReady] = useState<boolean>(false);
  const { setLogicalUnitTitle, setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const [initLUData, setInitLUData] = useState<U3143InitLU>({ launchSearch: false, hideSearch: true, nbRecordPerPage: 50 });

  // charge fichiers de traduction
  useEffect(() => {
    i18next.loadNamespaces(["u3143"], (err) => {
      if (err) return console.error("Error loading namespace:", err);
      setI18nReady(true);
    });
  }, [i18n]);

  // Gestion de l'initialisation de l'unité logique
  const handleInitLU = useU2000InitLUHandlers<U3143InitLU>({
    doInitLUFn: async () => {
      return await doInitLU(u2000AxiosAuthenticated);
    },
    onSuccess: (initLU: U3143InitLU) => {
      setInitLUData(initLU);
      setInitLUReady(true);
    },
    onError: (error: any) => {
      const errorResult = U2000HandleApiError(error, t, NS, districtCadastreHandleApiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
      setInitLUReady(true);
    },
  });

  // Initialisation de l'unité logique au montage du composant et apres quelques changements de langue
  useEffect(() => {
    handleInitLU.getInitLU();
  }, [t]);

  // Définit le titre et le breadcrumb pour l'unité logique
  useEffect(() => {
    if (!i18nReady || !initLUReady) return;

    const title = t("title", { ns: NS });
    setLogicalUnitTitle(title);

    return () => {
      setLogicalUnitTitle(null);
      setLogicalUnitBreadcrumb([]);
    };
  }, [i18nReady, initLUReady, t, setLogicalUnitTitle, setLogicalUnitBreadcrumb]);

  if (!i18nReady || !initLUReady) return null;

  return (
    <U3143ContextProvider initLU={initLUData}>
      <Outlet />
    </U3143ContextProvider>
  );
};

export default U3143;
